import * as React from 'react';

import type { Filter, HeaderLevel } from '@coursera-web/cds-labs';
import { CollectionsModule, getModuleLayoutConfig } from '@coursera-web/cds-labs';

import type { ButtonProps } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { useCampaignModulesData } from 'bundles/campaigns/context/CampaignModulesDataContext';
import useCollection from 'bundles/campaigns/hooks/useCollection';

type Props = {
  headlineComponent?: HeaderLevel;
};

export const useShouldShowCampaignCollectionsModule = () => {
  const { campaignData, loading } = useCampaignModulesData();
  return !!campaignData?.collectionsModule?.contentData || loading;
};

const CampaignHeroModuleRenderer = ({ headlineComponent }: Props) => {
  const track = useTracker();
  const [currentCollectionSetIndex, setCurrentCollectionSetIndex] = React.useState(0);

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        // TODO: This is the sectionName for the hero banner. Update this to the correct sectionName for the collections module.
        sectionName: 'hero_banner',
      },
    }
  );

  const content = useCampaignModulesData()?.campaignData?.collectionsModule?.contentData;

  const {
    description,
    ctaText,
    ctaTarget,
    ctaVariant,
    headline,
    showAllEntries,
    numOfEntriesPerCollection,
    collectionSetsCollection,
  } = content || {};

  const trackClick = () => {
    track('click_button', {
      // TODO: Update this to the correct button name for the collections module.
      button: { name: 'promo_cta', linkURL: ctaTarget ?? '' },
      pageSection: {
        // TODO: Update this to the correct sectionName for the collections module.
        sectionName: 'promo_template_collections',
      },
    });
  };

  const collectionSets = collectionSetsCollection?.items ?? [];
  const currentCollectionSet = collectionSets[currentCollectionSetIndex];
  const { loading: isCollectionLoading, collection } = useCollection({
    queryCollectionId: currentCollectionSet?.collectionModuleId ?? '',
    additionalInput: {
      preset_collection_id: currentCollectionSet?.presetCollectionId ?? '',
    },
  });

  if (!useShouldShowCampaignCollectionsModule() || !content) {
    return null;
  }

  const filters: Filter[] = [];
  collectionSets.forEach((collectionSet) => {
    if (collectionSet?.filterName) {
      filters.push({
        id: collectionSet.sys.id,
        filterName: collectionSet.filterName,
        filterImage: collectionSet.filterImage,
      });
    }
  });

  const filterTrackingName = (filterName: string) => {
    return `collections_filter_chip_${filterName
      .toLocaleLowerCase()
      .split(/\s&\s|\s+/)
      .join('_')
      .replace(/-/, '_')}`;
  };

  const handleFilterChange = (index: number, filterName: string) => {
    track('click_button', {
      button: {
        name: 'collections_filter_chip',
        linkName: filterTrackingName(filterName),
      },
      pageSection: {
        // TODO: Update this to the correct sectionName for the collections module.
        sectionName: 'promo_template_collections',
      },
    });
    setCurrentCollectionSetIndex(index);
  };

  return (
    <div ref={visibilityTrackingRef}>
      <CollectionsModule
        {...getModuleLayoutConfig(content)}
        {...{
          filters,
          currentCollectionSetIndex,
          collections: collection ? [collection] : undefined,
          isCollectionsLoading: isCollectionLoading,
          onFilterChange: handleFilterChange,
          showAllEntries,
          description,
          headline,
          headlineComponent,
          numOfEntriesPerCollection: numOfEntriesPerCollection ?? undefined,
          cta: {
            target: ctaTarget,
            variant: ctaVariant as ButtonProps['variant'],
            text: ctaText,
            onClick: trackClick,
          },
        }}
      />
    </div>
  );
};

export default CampaignHeroModuleRenderer;
